import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  RotateCcw,
  Calendar,
  Eye,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  ArrowUpRight,
  ArrowDownLeft,
  Boxes,
  IndianRupee,
  Package,
  ShieldCheck,
  Tag,
  Ban,
  Check,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DataTable,
  StandardImage,
  CompactStatCard,
  StatsGrid,
  type DataTableColumn,
  type DataTablePaginationMeta,
} from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/services/httpClient";
import {
  channelOrdersService,
  type ChannelOrder,
} from "@/services/channelOrdersService";

const payments = [
  { value: "0", label: "P2P" },
  { value: "1", label: "Cash" },
  { value: "2", label: "Online" },
];

function formatInr(amount: number) {
  return `₹${Number(amount || 0).toLocaleString("en-IN")}`;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}

function getStatusBadge(statusText: string) {
  switch (statusText?.toLowerCase()) {
    case "approved":
      return (
        <Badge
          variant="outline"
          className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 text-xs gap-1 font-semibold"
        >
          <CheckCircle2 className="h-3 w-3" />
          Approved
        </Badge>
      );
    case "rejected":
      return (
        <Badge
          variant="outline"
          className="bg-destructive/10 text-destructive border-destructive/20 text-xs gap-1 font-semibold"
        >
          <XCircle className="h-3 w-3" />
          Rejected
        </Badge>
      );
    case "cancelled":
      return (
        <Badge
          variant="outline"
          className="bg-muted text-muted-foreground border-border text-xs gap-1 font-semibold"
        >
          <Ban className="h-3 w-3" />
          Cancelled
        </Badge>
      );
    case "pending":
    default:
      return (
        <Badge
          variant="outline"
          className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 text-xs gap-1 font-semibold"
        >
          <Clock className="h-3 w-3" />
          Pending
        </Badge>
      );
  }
}

interface OrderTableSectionProps {
  kind: "outgoing" | "incoming";
  mode: "party" | "admin";
  title: string;
  description: string;
  onRefresh: () => void;
}

function OrderTableSection({
  kind,
  mode,
  title,
  description,
  onRefresh,
}: OrderTableSectionProps) {
  const { toast } = useToast();

  // Search & Filter state
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [appliedDateFrom, setAppliedDateFrom] = useState("");
  const [appliedDateTo, setAppliedDateTo] = useState("");

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Selected Order for Details Modal
  const [selectedOrder, setSelectedOrder] = useState<ChannelOrder | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Rejection dialog state
  const [orderToReject, setOrderToReject] = useState<ChannelOrder | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");

  // Cancellation dialog state
  const [orderToCancel, setOrderToCancel] = useState<ChannelOrder | null>(null);

  // Fetch orders with backend filtering
  const ordersQuery = useQuery({
    queryKey: [
      "channel",
      "orders",
      kind,
      mode,
      appliedSearch,
      statusFilter,
      appliedDateFrom,
      appliedDateTo,
    ],
    queryFn: () => {
      const params = {
        search: appliedSearch || undefined,
        status: statusFilter !== "all" ? statusFilter : undefined,
        fromDate: appliedDateFrom || undefined,
        toDate: appliedDateTo || undefined,
      };
      return kind === "outgoing"
        ? channelOrdersService.outgoing(params)
        : channelOrdersService.incoming(params);
    },
  });

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [appliedSearch, statusFilter, appliedDateFrom, appliedDateTo]);

  // Mutations
  const approveMutation = useMutation({
    mutationFn: (id: number) => channelOrdersService.approve(id),
    onSuccess: (res) => {
      toast({ title: `Order #${res.id} approved successfully` });
      onRefresh();
      ordersQuery.refetch();
    },
    onError: (err) =>
      toast({ title: getApiErrorMessage(err), variant: "destructive" }),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      channelOrdersService.reject(id, reason),
    onSuccess: (res) => {
      toast({ title: `Order #${res.id} rejected` });
      setOrderToReject(null);
      setRejectionReason("");
      onRefresh();
      ordersQuery.refetch();
    },
    onError: (err) =>
      toast({ title: getApiErrorMessage(err), variant: "destructive" }),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: number) => channelOrdersService.cancel(id),
    onSuccess: (res) => {
      toast({ title: `Order #${res.id} cancelled` });
      setOrderToCancel(null);
      onRefresh();
      ordersQuery.refetch();
    },
    onError: (err) =>
      toast({ title: getApiErrorMessage(err), variant: "destructive" }),
  });

  const rows = ordersQuery.data ?? [];

  // Summary Metrics
  const stats = useMemo(() => {
    const totalCount = rows.length;
    const totalAmount = rows.reduce(
      (sum, o) => sum + (o.totalAmount || o.unitPrice * o.quantity),
      0
    );
    const pendingCount = rows.filter((o) => o.statusText === "Pending").length;
    const approvedCount = rows.filter((o) => o.statusText === "Approved").length;

    return { totalCount, totalAmount, pendingCount, approvedCount };
  }, [rows]);

  // Filter Form Handlers
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput.trim());
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setPage(1);
  };

  const handleReset = () => {
    setSearchInput("");
    setAppliedSearch("");
    setStatusFilter("all");
    setDateFrom("");
    setDateTo("");
    setAppliedDateFrom("");
    setAppliedDateTo("");
    setPage(1);
  };

  const isFiltered = Boolean(
    appliedSearch ||
      statusFilter !== "all" ||
      appliedDateFrom ||
      appliedDateTo
  );

  // Pagination calculation
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const paginationMeta = useMemo<DataTablePaginationMeta>(
    () => ({
      page,
      pageSize,
      totalCount: rows.length,
      totalPages: Math.ceil(rows.length / pageSize) || 1,
      from: rows.length === 0 ? 0 : (page - 1) * pageSize + 1,
      to: Math.min(page * pageSize, rows.length),
    }),
    [page, pageSize, rows.length]
  );

  // DataTable columns
  const columns: DataTableColumn<ChannelOrder>[] = [
    {
      id: "id",
      header: "Order ID",
      accessorKey: "id",
      sortable: true,
      mobilePriority: 1,
      cell: (order) => (
        <Badge variant="outline" className="font-mono text-xs font-bold bg-muted/60">
          #ORD-{order.id}
        </Badge>
      ),
    },
    {
      id: "product",
      header: "Product",
      accessorKey: "productName",
      sortable: true,
      mobilePriority: 2,
      cell: (order) => (
        <div className="flex items-center gap-2.5 min-w-[200px]">
          <StandardImage
            src={order.productImageUrl}
            alt={order.productName}
            standard="product"
            className="h-10 w-10 rounded-md border object-cover shrink-0 cursor-pointer hover:opacity-80"
          />
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => {
                setSelectedOrder(order);
                setIsDetailOpen(true);
              }}
              className="font-semibold text-sm text-foreground hover:text-primary transition-colors text-left line-clamp-2"
            >
              {order.productName}
            </button>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
              {order.productSku && (
                <span className="font-mono text-[11px]">SKU: {order.productSku}</span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "party",
      header: kind === "incoming" ? "Supplier" : "Buyer",
      mobileLabel: kind === "incoming" ? "Supplier" : "Buyer",
      mobilePriority: 3,
      cell: (order) => {
        const partyName = kind === "incoming" ? order.sellerName : order.buyerName;
        const partyLevel = kind === "incoming" ? order.sellerLevel : order.buyerLevel;
        const phone = kind === "incoming" ? order.sellerPhone : order.buyerPhone;

        return (
          <div className="min-w-[130px] space-y-0.5">
            <p className="font-semibold text-sm text-foreground">{partyName}</p>
            <div className="flex flex-wrap items-center gap-1 text-[11px] text-muted-foreground">
              {partyLevel && (
                <Badge variant="secondary" className="text-[10px] py-0 px-1 font-medium">
                  {partyLevel}
                </Badge>
              )}
              {phone && <span>· {phone}</span>}
            </div>
          </div>
        );
      },
    },
    {
      id: "quantity",
      header: "Qty",
      accessorKey: "quantity",
      align: "center",
      sortable: true,
      mobileLabel: "Quantity",
      cell: (order) => (
        <span className="font-bold text-sm">{order.quantity}</span>
      ),
    },
    {
      id: "price",
      header: "Unit Price",
      accessorKey: "unitPrice",
      align: "right",
      sortable: true,
      cell: (order) => (
        <span className="text-sm font-medium">{formatInr(order.unitPrice)}</span>
      ),
    },
    {
      id: "total",
      header: "Total Amount",
      align: "right",
      sortable: true,
      mobilePriority: 4,
      cell: (order) => (
        <span className="font-extrabold text-sm text-foreground">
          {formatInr(order.totalAmount || order.unitPrice * order.quantity)}
        </span>
      ),
    },
    {
      id: "payment",
      header: "Payment",
      cell: (order) => (
        <div className="space-y-0.5 text-xs min-w-[100px]">
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="text-[10px] py-0 px-1 font-medium">
              {order.paymentMethodText || "P2P"}
            </Badge>
            <span
              className={
                order.paymentStatusText === "Received"
                  ? "text-emerald-600 font-semibold text-[11px]"
                  : "text-amber-600 font-medium text-[11px]"
              }
            >
              {order.paymentStatusText || "Pending"}
            </span>
          </div>
          {order.paymentReference && (
            <p className="text-[10px] font-mono text-muted-foreground truncate max-w-[120px]" title={order.paymentReference}>
              Ref: {order.paymentReference}
            </p>
          )}
        </div>
      ),
    },
    {
      id: "date",
      header: "Date",
      accessorKey: "createdAt",
      sortable: true,
      cell: (order) => (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {formatDate(order.createdAt)}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorKey: "statusText",
      align: "center",
      sortable: true,
      mobilePriority: 5,
      cell: (order) => getStatusBadge(order.statusText),
    },
    {
      id: "actions",
      header: "Actions",
      align: "center",
      mobilePriority: 6,
      cell: (order) => {
        const isPending = order.statusText === "Pending";

        return (
          <div className="flex items-center justify-center gap-1.5 min-w-[120px]">
            <Button
              size="sm"
              variant="outline"
              className="h-8 px-2 text-xs gap-1"
              onClick={() => {
                setSelectedOrder(order);
                setIsDetailOpen(true);
              }}
              title="View full order details"
            >
              <Eye className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">View</span>
            </Button>

            {/* Incoming pending action: Cancel (Buyer can cancel order placed with supplier) */}
            {kind === "incoming" && isPending && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 px-2 text-xs text-destructive hover:bg-destructive/10 border-destructive/30"
                onClick={() => setOrderToCancel(order)}
                title="Cancel this pending order"
              >
                Cancel
              </Button>
            )}

            {/* Outgoing pending actions: Approve / Reject (Seller approves/rejects downline order) */}
            {kind === "outgoing" && isPending && (
              <>
                <Button
                  size="sm"
                  className="h-8 px-2.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white gap-1"
                  disabled={approveMutation.isPending}
                  onClick={() => approveMutation.mutate(order.id)}
                  title="Approve order and transfer channel stock"
                >
                  <Check className="h-3.5 w-3.5" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-2 text-xs text-destructive hover:bg-destructive/10 border-destructive/30 gap-1"
                  onClick={() => {
                    setOrderToReject(order);
                    setRejectionReason("");
                  }}
                  title="Reject order"
                >
                  <X className="h-3.5 w-3.5" />
                  Reject
                </Button>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-5">
      {/* Metrics Row */}
      <StatsGrid columns={4}>
        <CompactStatCard
          title="Total Orders"
          value={stats.totalCount}
          icon={Package}
          className="bg-card"
        />
        <CompactStatCard
          title="Total Order Value"
          value={formatInr(stats.totalAmount)}
          icon={IndianRupee}
          className="bg-card"
        />
        <CompactStatCard
          title="Pending Approval"
          value={stats.pendingCount}
          icon={Clock}
          className="bg-card"
        />
        <CompactStatCard
          title="Approved Orders"
          value={stats.approvedCount}
          icon={CheckCircle2}
          className="bg-card"
        />
      </StatsGrid>

      {/* Filter and Search Bar */}
      <Card className="border border-border/80 shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <form onSubmit={handleSearchSubmit} className="space-y-3">
            <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
              {/* Search box */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search Order ID, Product, SKU, Buyer/Seller Name, Note..."
                  className="pl-10 h-10 w-full bg-background text-sm"
                />
              </div>

              {/* Date Filters */}
              <div className="flex items-center gap-2 shrink-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">From:</span>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-10 w-[140px] text-xs bg-background"
                  />
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">To:</span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-10 w-[140px] text-xs bg-background"
                  />
                </div>
              </div>

              {/* Order Status Select */}
              <div className="w-full sm:w-[150px] shrink-0">
                <Select
                  value={statusFilter}
                  onValueChange={(val) => {
                    setStatusFilter(val);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="h-10 bg-background text-sm">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                    <SelectItem value="Cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 shrink-0">
                <Button type="submit" className="h-10 px-4 gap-2">
                  <Search className="h-4 w-4" />
                  Search
                </Button>
                {isFiltered && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleReset}
                    className="h-10 px-3 gap-1.5 text-muted-foreground hover:text-foreground"
                    title="Reset all filters"
                  >
                    <RotateCcw className="h-3.5 w-3.5" />
                    Reset
                  </Button>
                )}
              </div>
            </div>

            {/* Active Filters Badges Bar */}
            {isFiltered && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Active Filters:</span>
                {appliedSearch && (
                  <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                    Search: &ldquo;{appliedSearch}&rdquo;
                    <button
                      type="button"
                      onClick={() => {
                        setSearchInput("");
                        setAppliedSearch("");
                      }}
                      className="ml-1 hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {appliedDateFrom && (
                  <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                    From: {appliedDateFrom}
                    <button
                      type="button"
                      onClick={() => {
                        setDateFrom("");
                        setAppliedDateFrom("");
                      }}
                      className="ml-1 hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {appliedDateTo && (
                  <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                    To: {appliedDateTo}
                    <button
                      type="button"
                      onClick={() => {
                        setDateTo("");
                        setAppliedDateTo("");
                      }}
                      className="ml-1 hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                {statusFilter !== "all" && (
                  <Badge variant="secondary" className="gap-1 font-normal py-0.5">
                    Status: {statusFilter}
                    <button
                      type="button"
                      onClick={() => setStatusFilter("all")}
                      className="ml-1 hover:text-foreground"
                    >
                      ×
                    </button>
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  className="h-6 px-2 text-xs text-primary hover:text-primary"
                >
                  Clear all
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Orders DataTable */}
      <Card className="border border-border/80 shadow-sm overflow-hidden">
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <CardTitle className="text-base font-bold text-foreground">
                {title} ({rows.length})
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">
                {description}
              </CardDescription>
            </div>
            {stats.pendingCount > 0 && (
              <Badge
                variant="outline"
                className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 gap-1 w-fit"
              >
                <Clock className="h-3 w-3" />
                {stats.pendingCount} pending approval
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <DataTable<ChannelOrder>
            columns={columns}
            data={pagedRows}
            getRowId={(row) => String(row.id)}
            loading={ordersQuery.isLoading}
            pagination={paginationMeta}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            emptyMessage={
              isFiltered
                ? "No orders match your search, status or date filters. Try resetting the filters."
                : kind === "outgoing"
                ? "You have not placed any orders yet. Browse the catalogue to order stock."
                : "No orders have been received yet."
            }
            viewMode="responsive"
            cardClassName="border-0 shadow-none rounded-none"
            pageSizeOptions={[10, 20, 50]}
          />
        </CardContent>
      </Card>

      {/* Order Details Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-6 rounded-2xl">
          {selectedOrder && (
            <div className="space-y-5">
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono font-bold text-xs bg-muted">
                    #ORD-{selectedOrder.id}
                  </Badge>
                  {getStatusBadge(selectedOrder.statusText)}
                </div>
                <DialogTitle className="text-lg font-bold mt-2">
                  Order Details
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Placed on {formatDate(selectedOrder.createdAt)}
                  {selectedOrder.approvedAt && ` · Approved on ${formatDate(selectedOrder.approvedAt)}`}
                </DialogDescription>
              </DialogHeader>

              {/* Product Info */}
              <div className="flex items-start gap-3 p-3.5 rounded-xl border bg-muted/20">
                <StandardImage
                  src={selectedOrder.productImageUrl}
                  alt={selectedOrder.productName}
                  standard="product"
                  className="h-16 w-16 rounded-lg border object-cover bg-background shrink-0"
                />
                <div className="space-y-1 min-w-0 flex-1">
                  <h4 className="font-bold text-sm text-foreground line-clamp-2">
                    {selectedOrder.productName}
                  </h4>
                  {selectedOrder.productSku && (
                    <p className="text-xs font-mono text-muted-foreground">
                      SKU: {selectedOrder.productSku}
                    </p>
                  )}
                  <p className="text-xs font-semibold text-primary">
                    Product ID: {selectedOrder.productId}
                  </p>
                </div>
              </div>

              {/* Pricing & Quantities Grid */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="p-3 rounded-lg border bg-card space-y-0.5">
                  <span className="text-muted-foreground">Quantity</span>
                  <p className="font-bold text-base text-foreground">
                    {selectedOrder.quantity} units
                  </p>
                </div>
                <div className="p-3 rounded-lg border bg-card space-y-0.5">
                  <span className="text-muted-foreground">Unit Price</span>
                  <p className="font-bold text-base text-foreground">
                    {formatInr(selectedOrder.unitPrice)}
                  </p>
                </div>
                <div className="p-3 rounded-lg border bg-card space-y-0.5">
                  <span className="text-muted-foreground">Total Order Value</span>
                  <p className="font-extrabold text-base text-primary">
                    {formatInr(selectedOrder.totalAmount || selectedOrder.unitPrice * selectedOrder.quantity)}
                  </p>
                </div>
                <div className="p-3 rounded-lg border bg-card space-y-0.5">
                  <span className="text-muted-foreground">Business Volume (BV)</span>
                  <p className="font-bold text-base text-amber-600 dark:text-amber-400">
                    {selectedOrder.businessVolume} BV
                  </p>
                </div>
              </div>

              {/* Buyer & Seller Parties */}
              <div className="rounded-xl border p-3.5 space-y-2.5 text-xs">
                <div className="flex items-start justify-between border-b pb-2">
                  <div>
                    <span className="text-muted-foreground font-medium">Buyer:</span>
                    <p className="font-bold text-sm text-foreground">{selectedOrder.buyerName}</p>
                    {selectedOrder.buyerPhone && (
                      <p className="text-muted-foreground text-[11px]">{selectedOrder.buyerPhone}</p>
                    )}
                  </div>
                  {selectedOrder.buyerLevel && (
                    <Badge variant="secondary" className="text-[10px]">
                      {selectedOrder.buyerLevel}
                    </Badge>
                  )}
                </div>
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-muted-foreground font-medium">Seller / Supplier:</span>
                    <p className="font-bold text-sm text-foreground">{selectedOrder.sellerName}</p>
                    {selectedOrder.sellerPhone && (
                      <p className="text-muted-foreground text-[11px]">{selectedOrder.sellerPhone}</p>
                    )}
                  </div>
                  {selectedOrder.sellerLevel && (
                    <Badge variant="secondary" className="text-[10px]">
                      {selectedOrder.sellerLevel}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Commission Details */}
              {selectedOrder.commissionPercent > 0 && (
                <div className="p-3 rounded-xl bg-primary/5 border border-primary/20 text-xs space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-primary">Seller Commission</span>
                    <span className="font-bold text-primary">
                      {formatInr(selectedOrder.commissionAmount)} ({selectedOrder.commissionPercent}%)
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Credited to seller account upon approval.
                  </p>
                </div>
              )}

              {/* Payment Details */}
              <div className="p-3.5 rounded-xl border bg-muted/20 space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Method:</span>
                  <span className="font-semibold text-foreground">{selectedOrder.paymentMethodText}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Status:</span>
                  <span className="font-semibold text-foreground">{selectedOrder.paymentStatusText}</span>
                </div>
                {selectedOrder.paymentReference && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Payment Reference:</span>
                    <span className="font-mono font-semibold text-foreground">{selectedOrder.paymentReference}</span>
                  </div>
                )}
                {selectedOrder.note && (
                  <div className="border-t pt-1.5 mt-1.5">
                    <span className="text-muted-foreground">Order Note:</span>
                    <p className="font-normal text-foreground mt-0.5">{selectedOrder.note}</p>
                  </div>
                )}
                {selectedOrder.rejectionReason && (
                  <div className="border-t pt-1.5 mt-1.5 text-destructive">
                    <span className="font-semibold">Rejection Reason:</span>
                    <p className="mt-0.5">{selectedOrder.rejectionReason}</p>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Reason Dialog */}
      <Dialog open={Boolean(orderToReject)} onOpenChange={(open) => !open && setOrderToReject(null)}>
        <DialogContent className="max-w-md p-6 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Reject Order #{orderToReject?.id}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Please provide a reason for rejecting this order so the buyer is informed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Label htmlFor="rejection-reason" className="text-xs font-semibold">
              Rejection Reason <span className="text-destructive">*</span>
            </Label>
            <Input
              id="rejection-reason"
              placeholder="e.g. Out of stock, Incorrect address, Payment unverified"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              className="text-sm"
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setOrderToReject(null)}
              disabled={rejectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectionReason.trim() || rejectMutation.isPending}
              onClick={() => {
                if (orderToReject) {
                  rejectMutation.mutate({
                    id: orderToReject.id,
                    reason: rejectionReason.trim(),
                  });
                }
              }}
            >
              {rejectMutation.isPending ? "Rejecting…" : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Order Dialog */}
      <Dialog open={Boolean(orderToCancel)} onOpenChange={(open) => !open && setOrderToCancel(null)}>
        <DialogContent className="max-w-md p-6 rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <Ban className="h-5 w-5 text-destructive" />
              Cancel Order #{orderToCancel?.id}
            </DialogTitle>
            <DialogDescription className="text-xs">
              Are you sure you want to cancel this pending order? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setOrderToCancel(null)}
              disabled={cancelMutation.isPending}
            >
              Keep Order
            </Button>
            <Button
              variant="destructive"
              disabled={cancelMutation.isPending}
              onClick={() => {
                if (orderToCancel) {
                  cancelMutation.mutate(orderToCancel.id);
                }
              }}
            >
              {cancelMutation.isPending ? "Cancelling…" : "Yes, Cancel Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type ChannelOrderSection = "place" | "incoming" | "outgoing" | "commission";

export function ChannelOrdersPanel({
  mode,
  sections,
}: {
  mode: "party" | "admin";
  sections?: ChannelOrderSection[];
}) {
  const show = (section: ChannelOrderSection) =>
    mode === "admin"
      ? section === "incoming"
      : !sections || sections.includes(section);

  const queryClient = useQueryClient();
  // Default to outgoing orders tab when available as required
  const [activeOrderTab, setActiveOrderTab] = useState<"outgoing" | "incoming">(
    show("outgoing") ? "outgoing" : "incoming"
  );
  const hasBothOrderSections = show("incoming") && show("outgoing");

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ["channel", "orders"] });
    queryClient.invalidateQueries({ queryKey: ["channel", "catalogue"] });
    queryClient.invalidateQueries({ queryKey: ["channel", "commission"] });
    queryClient.invalidateQueries({ queryKey: ["channel", "stock"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "channel-stock"] });
  };

  return (
    <div className="space-y-6">
      {hasBothOrderSections ? (
        <Tabs
          value={activeOrderTab}
          onValueChange={(val) => setActiveOrderTab(val as "outgoing" | "incoming")}
          className="space-y-4"
        >
          <TabsList className="h-11 p-1 bg-muted/80 rounded-xl">
            <TabsTrigger
              value="outgoing"
              className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-background data-[state=active]:shadow-sm gap-2"
            >
              <ArrowUpRight className="h-4 w-4" />
              Outgoing Orders (Sales to Downline)
            </TabsTrigger>
            <TabsTrigger
              value="incoming"
              className="rounded-lg px-4 py-2 text-sm font-semibold data-[state=active]:bg-background data-[state=active]:shadow-sm gap-2"
            >
              <ArrowDownLeft className="h-4 w-4" />
              Incoming Orders (Purchases from Upline)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="outgoing" className="mt-0">
            <OrderTableSection
              kind="outgoing"
              mode={mode}
              title={mode === "admin" ? "Orders Received by Admin" : "Outgoing Orders to Downline"}
              description="Orders placed by your downline buyers. Review and approve to transfer channel stock."
              onRefresh={refreshAll}
            />
          </TabsContent>

          <TabsContent value="incoming" className="mt-0">
            <OrderTableSection
              kind="incoming"
              mode={mode}
              title="Incoming Orders"
              description="Orders placed with your upline supplier. Stock is credited once the supplier approves."
              onRefresh={refreshAll}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <>
          {show("incoming") && (
            <OrderTableSection
              kind="incoming"
              mode={mode}
              title={mode === "admin" ? "Orders to Admin" : "Incoming Orders"}
              description="Orders placed by your downline. Approve to transfer channel stock and earn commission."
              onRefresh={refreshAll}
            />
          )}

          {show("outgoing") && (
            <OrderTableSection
              kind="outgoing"
              mode={mode}
              title="Your Channel Orders"
              description="Orders placed with your supplier. Once approved, the stock will be credited to your inventory."
              onRefresh={refreshAll}
            />
          )}
        </>
      )}
    </div>
  );
}
